import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Link,
  Chip,
  Grid,
  Paper,
  TextField,
  Select,
  MenuItem,
  Skeleton,
  Button,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { getAnimeReviewsByUser, AnimeReviewDetail } from "../services/animeApi";
import Pagination from "@mui/material/Pagination";
import { Link as RouterLink, useSearchParams, useParams } from "react-router-dom";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AdvertisementResponsive from "../components/advertisement/Ad_GA_res";
import { formatReviewText } from "../utils/formatText";
import { useTheme, useMediaQuery } from "@mui/material";
import { debounce } from 'lodash';
import { SelectChangeEvent } from '@mui/material';
import SortIcon from "@mui/icons-material/Sort";
import Clear from '@mui/icons-material/Clear';
import { animeScoreTags } from '../utils/animeScoreTags';
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';
import DetailScores, { ScoreItem } from './review/DetailScores';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import MovieFilterOutlinedIcon from '@mui/icons-material/MovieFilterOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TheaterComedyOutlinedIcon from '@mui/icons-material/TheaterComedyOutlined';
import TheatersOutlinedIcon from '@mui/icons-material/TheatersOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';

interface AnimeReviewsTabProps {
  username?: string;
  displayName?: string;
  isPublic?: boolean;
}

const WATCH_MEDIUM_LABELS: Record<number, string> = {
  1: '劇場',
  2: 'リバイバル',
  3: 'TV放送',
  4: 'ストリーミング',
  5: 'パッケージ',
};

const WATCH_STYLE_LABELS: Record<number, string> = {
  1: 'リアルタイム',
  2: '再放送',
  3: '一気見',
  4: '断片視聴',
};

const getScoreColor = (score: number | null) => {
  if (score === null) return "#666666";
  if (score >= 4.5) return "#4CAF50";
  if (score >= 4.0) return "#8BC34A";
  if (score >= 3.5) return "#CDDC39";
  if (score >= 3.0) return "#FFEB3B";
  if (score >= 2.5) return "#FFC107";
  if (score >= 2.1) return "#FF9800";
  return "#F44336";
};

const AnimeReviewsTab: React.FC<AnimeReviewsTabProps> = ({ username: usernameProp, displayName, isPublic = false }) => {
  const { username: usernameParam } = useParams<{ username: string }>();
  const stableUsername = isPublic ? usernameParam : 'me';
  const username = isPublic ? usernameParam : usernameProp;
  const [searchParams, setSearchParams] = useSearchParams();
  const [expanded, setExpanded] = useState<(number | string)[]>([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('query') || '');
  const [sortOrder, setSortOrder] = useState(searchParams.get('order') || 'default');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const maxLength = { xs: 180, sm: 320 };
  const maxLines = 8;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const currentQuery = searchParams.get('query') || '';
  const currentOrder = searchParams.get('order') || 'default';

  const { data, isLoading } = useQuery({
    queryKey: ['animeReviews', stableUsername, page, currentQuery, currentOrder],
    queryFn: () => getAnimeReviewsByUser(username!, page, currentQuery, currentOrder),
    placeholderData: (previousData) => previousData,
    enabled: !!username,
    staleTime: isPublic ? 30000 : 2000,
  });

  const reviews: AnimeReviewDetail[] = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / 10);

  const debouncedSearch = useMemo(
    () => debounce((query: string) => {
      setPage(1);
      setSearchParams(params => {
        params.set('page', '1');
        if (query) params.set('query', query);
        else params.delete('query');
        return params;
      });
    }, 500),
    [setSearchParams]
  );

  const handleSortChange = (event: SelectChangeEvent) => {
    const newOrder = event.target.value;
    setSortOrder(newOrder);
    setPage(1);
    setSearchParams(params => {
      params.set('page', '1');
      if (newOrder !== 'default') params.set('order', newOrder);
      else params.delete('order');
      return params;
    });
  };

  const handleClearFilter = () => {
    setSearchQuery('');
    setSortOrder('default');
    setPage(1);
    setSearchParams(params => {
      params.set('page', '1');
      params.delete('query');
      params.delete('order');
      return params;
    });
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setSearchParams(params => {
      params.set('page', value.toString());
      return params;
    });
  };

  const orderDisplayText: { [key: string]: string } = {
    'default': '投稿日時 (新しい順)',
    'added_date_asc': '投稿日時 (古い順)',
    'score_desc': '評価 (高い順)',
    'score_asc': '評価 (低い順)',
    'release_date_desc': '放送開始日 (新しい順)',
    'release_date_asc': '放送開始日 (古い順)',
    'update_date_desc': '更新日時 (新しい順)',
    'update_date_asc': '更新日時 (古い順)',
  };

  const toggleExpand = (reviewId: number | string) => {
    setExpanded((prev) => [...prev, reviewId]);
  };

  const truncateText = (text: string) => {
    const lines = text.split("\n");
    const currentMaxLength = isMobile ? maxLength.xs : maxLength.sm;
    if (lines.length > maxLines || text.length > currentMaxLength) {
      const truncatedLines = lines.slice(0, maxLines);
      let truncatedText = truncatedLines.join("\n");
      if (truncatedText.length > currentMaxLength) {
        truncatedText = truncatedText.slice(0, currentMaxLength);
      }
      return `${truncatedText}...`;
    }
    return text;
  };

  const formatDate = (date: string | null) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString();
  };

  const getImageUrl = (imageUrl: string | null | undefined) => {
    return imageUrl || "/image/no_image.png";
  };

  const ReviewLoadingPlaceholder = () => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={120} height={24} />
          <Skeleton variant="text" width={80} height={20} />
        </Box>
      </Box>
      <Skeleton variant="rectangular" height={100} sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Skeleton variant="text" width={60} height={24} />
        <Skeleton variant="text" width={100} height={24} />
      </Box>
    </Box>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={8.5}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              placeholder="Search..."
              variant="standard"
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                debouncedSearch(e.target.value);
              }}
              sx={{
                flexGrow: 1,
                '& .MuiInputBase-input': { ...theme.typography.body2 },
                '& .MuiInputBase-input::placeholder': { opacity: 0.5 },
                '& .MuiInputBase-root:before': { borderBottomColor: 'rgba(255, 255, 255, 0.5)' },
                '& .MuiInputBase-root:hover:not(.Mui-disabled):before': { borderBottomColor: 'rgba(255, 255, 255, 0.8)' },
              }}
            />
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Select
                value={sortOrder}
                onChange={handleSortChange}
                displayEmpty
                sx={{
                  minWidth: 100,
                  ...theme.typography.body2,
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '4px',
                  '& .MuiSelect-select': {
                    paddingY: 0.5, paddingX: 1,
                    display: 'flex', alignItems: 'center',
                    color: 'text.secondary',
                    fontSize: { xs: '0.65rem', sm: '0.8rem' },
                  },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                }}
                renderValue={(selected) => {
                  const selectedOption = Object.entries(orderDisplayText).find(([key]) => key === selected);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <SortIcon sx={{ mr: 0.5, fontSize: '1rem', display: { xs: 'none', sm: 'block' } }} />
                      {selectedOption ? selectedOption[1] : 'デフォルト'}
                    </Box>
                  );
                }}
              >
                <MenuItem value="default" dense>投稿日時 (新しい順)</MenuItem>
                <MenuItem value="added_date_asc" dense>投稿日時 (古い順)</MenuItem>
                <MenuItem value="score_desc" dense>評価 (高い順)</MenuItem>
                <MenuItem value="score_asc" dense>評価 (低い順)</MenuItem>
                <MenuItem value="release_date_desc" dense>放送開始日 (新しい順)</MenuItem>
                <MenuItem value="release_date_asc" dense>放送開始日 (古い順)</MenuItem>
                <MenuItem value="update_date_desc" dense>更新日時 (新しい順)</MenuItem>
                <MenuItem value="update_date_asc" dense>更新日時 (古い順)</MenuItem>
              </Select>
            </Box>
          </Box>
          {searchQuery !== '' && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {totalCount}件のレビューが見つかりました
              </Typography>
              <Button
                size="small"
                onClick={handleClearFilter}
                startIcon={<Clear fontSize="small" />}
                sx={{ ...theme.typography.body2, textTransform: 'none', p: '2px 8px' }}
              >
                Clear filter
              </Button>
            </Box>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} sm={12} md={8.5}>
        <Box>
          {isLoading && !data ? (
            <Box>
              {[...Array(3)].map((_, index) => (
                <ReviewLoadingPlaceholder key={index} />
              ))}
            </Box>
          ) : (
            <>
              {reviews.length === 0 ? (
                <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="body1" color="text.secondary">
                      まだレビューがありません
                    </Typography>
                    {!isPublic && (
                      <Typography variant="body1" color="text.secondary">
                        アニメを観たら、レビューを書いてみましょう
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ) : (
                <>
                  {reviews.map((review) => {
                    const isExpanded = expanded.includes(review.id);
                    const displayText = isExpanded
                      ? review.review_text
                      : truncateText(review.review_text);
                    const currentMaxLength = isMobile ? maxLength.xs : maxLength.sm;
                    const shouldShowReadMore = !isExpanded && (
                      review.review_text.length > currentMaxLength ||
                      review.review_text.split('\n').length > maxLines
                    );

                    return (
                      <Box
                        key={review.id}
                        sx={{
                          mb: 2,
                          borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                          "&:hover": { transform: "none" },
                        }}
                      >
                        <Box sx={{ paddingBottom: { xs: 1, sm: 1.5 }, paddingTop: { xs: 1, sm: 1.5 } }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                            <Box
                              component={RouterLink}
                              to={`/anime/${review.mal_id}`}
                              sx={{ display: 'block', width: "20%", maxWidth: 120, mr: 1.5 }}
                            >
                              <Box
                                component="img"
                                src={getImageUrl(review.anime.image_url)}
                                alt={review.anime.title}
                                sx={{ width: "100%", borderRadius: 1, objectFit: "cover" }}
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Link
                                  component={RouterLink}
                                  to={`/anime/${review.mal_id}`}
                                  color="inherit"
                                  underline="hover"
                                  sx={{
                                    typography: "h6",
                                    display: "block",
                                    mb: 0.5,
                                    fontSize: { xs: "0.75rem!important", sm: "0.9rem!important" },
                                  }}
                                >
                                  {review.anime.title_japanese || review.anime.title}
                                </Link>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "flex-start", flexWrap: "wrap", gap: 1, mb: 1, flexDirection: { xs: "column", sm: "row" } }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "white",
                                      display: "flex", alignItems: "center",
                                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                      py: 0.25, px: 0.75, lineHeight: "1",
                                      borderRadius: 1,
                                      backgroundColor: `${getScoreColor(review.score)}B3`,
                                    }}
                                  >
                                    {review.score !== null ? review.score.toFixed(1) : "-"}
                                  </Typography>
                                  <Box sx={{ width: 80, height: 4, bgcolor: "rgba(255, 255, 255, 0.3)", borderRadius: 2, overflow: "hidden" }}>
                                    <Box sx={{
                                      width: review.score !== null ? `${((review.score - 1) / 4) * 100}%` : "100%",
                                      height: "100%",
                                      bgcolor: getScoreColor(review.score),
                                      borderRadius: 2,
                                      transition: "width 0.3s ease",
                                    }} />
                                  </Box>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                  <Typography variant="caption" sx={{ display: "inline-flex", alignItems: "center", color: "pulse.info" }}>
                                    <LiveTvOutlinedIcon fontSize="inherit" sx={{ mr: 0.5, fontSize: "0.85rem" }} />
                                    {(() => {
                                      const watched = review.episodes_watched;
                                      const total = review.anime.episodes;
                                      if (watched == null) return '-';
                                      if (total && total > 0) {
                                        const pct = Math.round((watched / total) * 100);
                                        return pct >= 100 ? `${pct}%` : `${pct}%/${watched}ep`;
                                      }
                                      return `${watched}ep`;
                                    })()}
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: "inline-flex", alignItems: "center", color: "pulse.info" }}>
                                    <PlayCircleOutlineIcon fontSize="inherit" sx={{ mr: 0.5, fontSize: "0.85rem" }} />
                                    {review.start_date
                                      ? `${formatDate(review.start_date)}${review.end_date ? ` - ${formatDate(review.end_date)}` : ''}`
                                      : '-'}
                                  </Typography>
                                  {review.watch_medium && review.watch_medium.length > 0 && (
                                    <Typography variant="caption" sx={{ display: "inline-flex", alignItems: "center", color: "pulse.info" }}>
                                      <TheatersOutlinedIcon fontSize="inherit" sx={{ mr: 0.5, fontSize: "0.85rem" }} />
                                      {review.watch_medium.map(id => WATCH_MEDIUM_LABELS[id] || '').filter(Boolean).join(' / ')}
                                    </Typography>
                                  )}
                                  {review.watch_style && review.watch_style.length > 0 && (
                                    <Typography variant="caption" sx={{ display: "inline-flex", alignItems: "center", color: "pulse.info" }}>
                                      <SpeedOutlinedIcon fontSize="inherit" sx={{ mr: 0.5, fontSize: "0.85rem" }} />
                                      {review.watch_style.map(id => WATCH_STYLE_LABELS[id] || '').filter(Boolean).join(' / ')}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              {/* Score Tags */}
                              {review.score_tags && review.score_tags.map((tagId) => {
                                const tag = animeScoreTags.find((t) => t.id === tagId);
                                return tag && (
                                  <Chip
                                    key={tag.id}
                                    icon={
                                      tag.id === 9 || tag.id >= 30 ? (
                                        <KeyboardDoubleArrowDownIcon style={{ color: "#79bcf3" }} />
                                      ) : (
                                        <KeyboardDoubleArrowUpIcon style={{ color: "#f37979" }} />
                                      )
                                    }
                                    label={tag.label}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      mr: 0.5, mb: 0.5, height: "1.1rem",
                                      "& .MuiChip-icon": { fontSize: "0.7rem" },
                                      "& .MuiChip-label": { fontSize: "0.6rem", py: 0.25 },
                                    }}
                                  />
                                );
                              })}
                              {/* Anime Detail Scores */}
                              <DetailScores
                                scores={[
                                  { key: 'score_story', label: 'STR', fullLabel: 'Story', icon: MenuBookOutlinedIcon, value: review.score_story },
                                  { key: 'score_animation', label: 'ANI', fullLabel: 'Animation', icon: MovieFilterOutlinedIcon, value: review.score_animation },
                                  { key: 'score_sound', label: 'SND', fullLabel: 'Sound', icon: VolumeUpIcon, value: review.score_sound },
                                  { key: 'score_character', label: 'CHR', fullLabel: 'Character', icon: PersonOutlineIcon, value: review.score_character },
                                  { key: 'score_direction', label: 'DIR', fullLabel: 'Direction', icon: TheaterComedyOutlinedIcon, value: review.score_direction },
                                ]}
                                variant="reviewsTab"
                              />
                              {/* Review Text */}
                              <Box sx={{
                                position: "relative", mt: 1, mb: 0,
                                '& .spoiler': {
                                  backgroundColor: '#444', padding: '0 4px', borderRadius: '2px', cursor: 'pointer',
                                  '&:hover': { backgroundColor: 'transparent' }
                                }
                              }}>
                                <Typography variant="body2" component="div" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: { xs: 1.3, sm: 1.3 } }}>
                                  {formatReviewText(displayText, isMobile)}
                                </Typography>
                                {shouldShowReadMore && (
                                  <Link
                                    component="button"
                                    onClick={() => toggleExpand(review.id)}
                                    sx={{
                                      position: "absolute", bottom: 0, right: 0,
                                      cursor: "pointer", bgcolor: "background.default",
                                      pl: 1, fontSize: "0.7rem",
                                    }}
                                  >
                                    全て読む
                                  </Link>
                                )}
                              </Box>
                              {/* Footer */}
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" color="text.secondary" lineHeight={1}>
                                    {new Date(review.created_at).toLocaleDateString()}に投稿
                                  </Typography>
                                </Box>
                                <Link
                                  component={RouterLink}
                                  to={`/anime/${review.mal_id}`}
                                  sx={{
                                    display: "flex", alignItems: "center",
                                    color: "pulse.info", textDecoration: "none", fontSize: "0.75rem",
                                    "&:hover": { color: "primary.main" },
                                  }}
                                >
                                  <OpenInNewIcon sx={{ fontSize: "1rem", ml: 0.5 }} />
                                </Link>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                  {totalPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                      <Pagination count={totalPages} page={page} onChange={handlePageChange} />
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} md={3.5} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Box sx={{ position: 'sticky', top: 80 }}>
          <AdvertisementResponsive />
        </Box>
      </Grid>
    </Grid>
  );
};

export default AnimeReviewsTab;
